import { Feedback, styled, TextInput } from 'canoe';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import get from 'lodash/get';
import has from 'lodash/has';
import isNumber from 'lodash/isNumber';
import React, { useEffect, useMemo } from 'react';

import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { DefinitionFieldWrapper } from './DefinitionFormComponents';

const InputWrapper = styled(DefinitionFieldWrapper)`
  min-width: 80px;
`;

interface DefinitionInputProps {
  name: string;
  type: 'text' | 'number';
  readOnly?: boolean;
  placeholder?: string;
}

export const DefinitionInput = ({ name, readOnly, placeholder, type }: DefinitionInputProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const currentValue = useMemo(() => {
    const value = get(values, name);

    // if the input type is numeric and it contains a non-numeric value, set as empty
    if (value !== '' && type === 'number' && !isNumber(value)) {
      return '';
    }

    // if the input type is text and it contains a numeric value, convert to string
    if (value !== '' && type === 'text' && isNumber(value)) {
      return value.toString();
    }

    return value;
  }, [values, name, type]);

  useEffect(() => {
    if (!has(values, name)) {
      setFieldValue(name, '', false);
    } else if (Array.isArray(currentValue)) {
      // if we are switching from multiselect to normal dropdown,
      // convert array to string by keeping the first value only
      setFieldValue(name, currentValue[0], false);
    }
  }, []);

  return (
    <Field name={name}>
      {({ field, meta }: FieldAttributes<any>) => (
        <InputWrapper>
          <TextInput
            {...field}
            data-cy="segment-definition-input"
            id={`definition-${name}`}
            value={currentValue}
            type={type}
            placeholder={placeholder}
            state={meta.touched && meta.error ? 'danger' : 'default'}
            disabled={readOnly}
          />
          {meta.touched && meta.error && (
            <Feedback mt="xxs" color="danger">
              {meta.error}
            </Feedback>
          )}
        </InputWrapper>
      )}
    </Field>
  );
};
